import {formatNumber} from "@progress/kendo-intl";

import {CategoryAxis} from "@progress/kendo-react-charts";
import {IBoardObjectNode} from "../types/IBoardObjectNode";
import {IWidgetData} from "../types/IWidgetData";
import {IBoardMeasure} from "../types/IBoardMeasure";
import { IChartSerie } from '../components/widget/chart/types/IChartSerie';
import { IChartSetup } from '../components/widget/chart/types/IChartSetup';
import { IGridData } from '../components/widget/chart/types/IGridData';
import { ITimePeriod } from '../types/ITimePeriod';

const useAxisChart = () => {
    let categoryMap = new Map<number, number>();

    const _clearEmptySeries = (series: IChartSerie[]) => {
        for (let i = series.length - 1; i > -1; i--) {
            let serie = series[i];
            if (serie?.data?.length === 0)
                series.splice(i, 1);
        }
    };

    const generateCategoriesSingle = (nodes: IBoardObjectNode[], forLocalCurrency: boolean): string[] => {
        categoryMap.clear();
        let categories: string[] = [];
        let objects = {};
        nodes.forEach((node, key) => {
            if (node.id && node.objectId) {
                let objectNode = objects[node.objectId];
                if (objectNode) {
                    categoryMap.set(node.id, objectNode);
                } else {
                    categories.push(node.name + (forLocalCurrency ? ' (' + node.currency + ')' : ''));
                    categoryMap.set(node.id, key);
                    objects[node.objectId] = key;
                }
            }
        });
        return categories;
    }

    const _seriesBaseOptions = (serie: IChartSerie, setup: IChartSetup): IChartSerie => {
        let _hasStack = false;

        if (setup.series?.find((s) => s.stacked))
            _hasStack = true;

        let stack: string | boolean = _hasStack && serie.name;
        if (serie.stacked)
            stack = serie.stackGroup || true;

        let serieOptions = {
            name: serie.name,
            type: serie.type || setup.type,
            axis: serie.axis && serie.axis,
            stack: stack,
            opacity: 1,
            data: [],
            lookupRef: {},
            field: 'data',
            line: {
                style: 'smooth',
            },
            markers: {},
            labels: {
                visible: setup.seriesLabels,
                background: 'transparent',
                position: 'outsideEnd',
                callbackTemplate: (e) => e.dataItem && formatNumber(e.dataItem.amount, e.dataItem.lookupRef.percentage ? 'p' + e.dataItem.lookupRef.scale : 'n' + e.dataItem.lookupRef.scale),
            },
            legend: {
                visible: setup.legend !== 'none',
                position: setup.legend,
            },
            tooltip: {
                visible: true,
                callbackTemplate: (e) => {
                    if (e.point) {
                        return (
                            e.point.category + ' ' + e.point.series.name + ': ' + formatNumber(
                                e.point.value,
                                e.point.dataItem.lookupRef.percentage ? 'p' + e.point.dataItem.lookupRef.scale : 'n' + e.point.dataItem.lookupRef.scale,
                            )
                        );
                    }
                },
            },
        };

        if (serieOptions.type === 'marker') {
            serieOptions.type = 'line';
            serieOptions.opacity = 0;
            serieOptions.markers = {
                type: 'circle',
                size: 10,
                border: {
                    width: 3,
                },
            };
        }

        return serieOptions;
    };

    const _registerGridRecord = (gridData: IGridData[], value: IWidgetData, periodHash: any, amount: number, measures: IBoardMeasure[]) => {
        let gridValue = periodHash[value['periodId']];

        let measure = measures.find((measure) => {
            return measure.id === value['measureId'];
        });

        if (!gridValue) {
            gridValue = {
                periodId: value['periodId'],
                periodName: value['periodName'],
                endDate: value['endDate'],
                isPercent: measure && measure.isPercent,
                scale: measure && measure.scale
            };
            periodHash[value['periodId']] = gridValue;
            gridData.push(gridValue);
        }

        gridValue['_' + value['nodeId'] + '_' + value['measureId']] = amount;
    };

    const _loadAxisDataSingle = (seriesHash: any, widgetData: IWidgetData[], measures: IBoardMeasure[], selectedPeriod?: ITimePeriod): IGridData[] | undefined => {
        if (!widgetData) return;

        let periodHash: any = {};
        let gridData: IGridData[] = [];

        for (let i = 0; i < widgetData.length; i++) {
            let value = widgetData[i];
            if (value && selectedPeriod && value['periodId'] !== selectedPeriod['id']) continue;

            let index = categoryMap.get(value['nodeId']);

            let series = seriesHash[value['measureId']];

            if (series && index !== undefined && index > -1) {
                let amount = value['amount'] / (series.sectionSeries.amountsIn || 1);

                series.chartSeries.data[index] = {
                    value: amount,
                    nodeId: value['nodeId'],
                    lookupRef: series.chartSeries.lookupRef,
                    amount: amount,
                };

                _registerGridRecord(gridData, value, periodHash, amount, measures);
            }
        }

        return gridData;
    };

    const generateValueAxes = (setup: IChartSetup): CategoryAxis[] => {
        let axes: CategoryAxis[] = [];

        setup.valueAxes?.forEach((axis) => {
            axes.push({
                name: axis.id,
                axisCrossingValue: setup.valueAxes && setup.valueAxes?.length > 1 ? -100000000000000 : [0, -100000000000000],
                title: {
                    text: axis.caption,
                },
                labels: {
                    format: (axis.isPercent ? 'p' : 'n') + axis.scale,
                    rotation: 'auto',
                },
            });
        })

        return axes;
    };

    const generateCategories = (periods, chartData) => {
        categoryMap.clear();

        let categories: string[] = [];

        periods?.forEach((period) => {
            if (chartData.find(dataItem => dataItem.periodId === period['id'])) {
                categoryMap.set(period['id'], categories.length);
                categories.push(period.name);
            }
        });

        return categories;
    };

    const generateSeriesSingle = (setup: IChartSetup, measures: IBoardMeasure[], widgetData: IWidgetData[], selectedPeriod?: ITimePeriod) => {
        let series: IChartSerie[] = [];
        let seriesHash: any = {};
        let sectionSerie = setup.series && setup.series[0];
        let measure = measures.find((m) => {
            return m.id?.toString() === sectionSerie?.measureId?.toString();
        });

        if (measure) {
            let serieOptions = sectionSerie && _seriesBaseOptions(sectionSerie, setup);
            if (serieOptions) {
                serieOptions.lookupRef = {measureId: sectionSerie?.measureId, amountsIn: sectionSerie?.amountsIn || 1};

                seriesHash = {
                    chartSeries: serieOptions,
                    sectionSeries: sectionSerie,
                };

                series.push(serieOptions);
            }
        }

        setup.series?.forEach((serie) => {
            let measure = measures.find((m) => m.id?.toString() === serie.measureId?.toString());

            if (measure) {
                let serieOptions = _seriesBaseOptions(serie, setup);
                let axs = setup.valueAxes?.find((e) => e.id === serie.axis);
                serieOptions.lookupRef = {
                    measureId: serie.measureId,
                    amountsIn: serie.amountsIn || 1,
                    viewCashflows: serie.viewCashflows,
                    scale: serie.scale,
                    percentage: axs ? axs.isPercent : false,
                };

                if (measure.id) {
                    seriesHash[measure.id] = {
                        chartSeries: serieOptions,
                        sectionSeries: serie,
                    };
                }

                series.push(serieOptions);
            }
        });

        let gridData = _loadAxisDataSingle(seriesHash, widgetData, measures, selectedPeriod);
        _clearEmptySeries(series);

        return {series, gridData};
    };

    const _loadAxisData = (seriesHash: any, widgetData: IWidgetData[], measures: IBoardMeasure[]): IGridData[] | undefined => {
        if (!widgetData) {
            return;
        }

        let periodHash: any = {};
        let gridData: IGridData[] = [];

        for (let i = 0; i < widgetData.length; i++) {
            let value = widgetData[i];

            let index = categoryMap.get(value['periodId']);

            let series = seriesHash[value['nodeId'] + '_' + value['measureId']];

            if (series && index !== undefined && index > -1) {
                let amount = value['amount'] / (series.sectionSeries.amountsIn || 1);

                series.chartSeries.data[index] = {
                    amount: amount,
                    lookupRef: series.chartSeries.lookupRef
                };

                _registerGridRecord(gridData, value, periodHash, amount, measures);
            }
        }

        return gridData;
    };

    const generateSeries = (setup: IChartSetup, nodes: IBoardObjectNode[], measures: IBoardMeasure[], widgetData: IWidgetData[], forLocalCurrency: boolean) => {
        let series: IChartSerie[] = [];
        let seriesHash: any = {};
        let multipleMeasures = true;

        nodes.forEach((node) => {
            const setupSeries = setup.series || [];

            for (let i = 0; i < setupSeries.length; i++) {
                let sectionSeries = setupSeries[i];

                let measure = measures.find((m) => {
                    return m.id?.toString() === sectionSeries.measureId?.toString();
                });

                if (!measure) continue;

                let serie = _seriesBaseOptions(sectionSeries, setup);
                serie.name = node.name + (multipleMeasures ? ': ' + sectionSeries.name : '') + (forLocalCurrency ? ' (' + node.currency + ')' : '');
                let axs = setup.valueAxes?.find((e) => e.id === sectionSeries.axis);
                serie.lookupRef = {
                    nodeId: node.id,
                    measureId: sectionSeries.measureId,
                    amountsIn: sectionSeries.amountsIn || 1,
                    viewCashflows: sectionSeries.viewCashflows,
                    scale: sectionSeries.scale,
                    percentage: axs ? axs.isPercent : false,
                };

                seriesHash[node.id + '_' + sectionSeries.measureId] = {
                    chartSeries: serie,
                    sectionSeries: sectionSeries,
                };

                series.push(serie);
            }
        });

        let gridData = _loadAxisData(seriesHash, widgetData, measures);
        _clearEmptySeries(series);

        return {series, gridData};
    };

    return {
        generateCategoriesSingle,
        generateSeriesSingle,
        generateValueAxes,
        generateCategories,
        generateSeries
    }
}

export default useAxisChart;